import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Header from '../components/Header';
import { quizSteps } from '../mock/quizData';
import MeasureStep from '../components/MeasureStep';
import TargetWeightStep from '../components/TargetWeightStep';
import AgeInputStep from '../components/AgeInputStep';
import { useLang } from '../context/LanguageContext';

const QuizPage = ({ answers, updateAnswer }) => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const { t, tr } = useLang();

  const step = quizSteps[stepIndex];
  const progress = useMemo(
    () => ((stepIndex + 1) / quizSteps.length) * 100,
    [stepIndex]
  );

  const goNext = (autoValue) => {
    if (autoValue !== undefined) {
      updateAnswer(step.id, autoValue);
    }
    if (stepIndex < quizSteps.length - 1) {
      setStepIndex((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/loading');
    }
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate('/');
    } else {
      setStepIndex((s) => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderContent = () => {
    if (step.type === 'image-select') {
      return (
        <div className="max-w-md mx-auto space-y-3">
          {step.options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => goNext(opt.id)}
              className="quiz-card w-full rounded-md overflow-hidden flex items-center justify-between text-left group"
              data-testid={`quiz-opt-${opt.id}`}
            >
              <div className="pl-6 py-5">
                <div className="text-xl font-bold text-white">{tr(opt.label)}</div>
              </div>
              <div className="w-40 h-32 shrink-0 overflow-hidden">
                <img
                  src={opt.image}
                  alt={tr(opt.label)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </button>
          ))}
        </div>
      );
    }

    if (step.type === 'single-select') {
      return (
        <div className="max-w-md mx-auto space-y-3">
          {step.options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => goNext(opt.id)}
              className="quiz-card w-full rounded-md px-5 py-4 flex items-center justify-between text-left"
              data-testid={`quiz-opt-${opt.id}`}
            >
              <div>
                <div className="text-base font-semibold text-white">{tr(opt.label)}</div>
                {opt.desc && (
                  <div className="text-sm text-white/60 mt-0.5">{tr(opt.desc)}</div>
                )}
              </div>
              <span className="w-9 h-9 flex items-center justify-center rounded-sm bg-[#0d0e10] border border-[#333] transition-all">
                <ArrowRight size={18} className="text-white" />
              </span>
            </button>
          ))}
        </div>
      );
    }

    if (step.type === 'multi-select') {
      const selected = answers[step.id] || [];
      const toggle = (id) => {
        const next = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id];
        updateAnswer(step.id, next);
      };

      return (
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-2 gap-3">
            {step.options.map((opt) => {
              const isSelected = selected.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  className={`quiz-card rounded-md px-4 py-5 flex items-center justify-between text-left ${
                    isSelected ? 'selected' : ''
                  }`}
                  data-testid={`quiz-opt-${opt.id}`}
                >
                  <span className="text-base font-semibold text-white">{tr(opt.label)}</span>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-sm border ${
                      isSelected
                        ? 'bg-[#ff4e15] border-[#ff4e15]'
                        : 'border-[#3a3b3f]'
                    } transition-all`}
                  >
                    {isSelected && <Check size={14} className="text-white" />}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => goNext()}
            disabled={selected.length === 0}
            className={`mt-6 w-full py-3.5 rounded-sm font-bold uppercase tracking-wide text-white transition-all ${
              selected.length === 0
                ? 'bg-[#2a2b2f] text-white/40 cursor-not-allowed'
                : 'bg-[#ff4e15] hover:bg-[#ff6033] pulse-orange'
            }`}
            data-testid="quiz-continue-btn"
          >
            {t('continue')}
          </button>
        </div>
      );
    }

    if (step.type === 'measurements') {
      return (
        <MeasureStep
          value={answers.measurements}
          onSave={(v) => goNext(v)}
        />
      );
    }

    if (step.type === 'target-weight') {
      return (
        <TargetWeightStep
          current={answers.measurements?.weight}
          unit={answers.measurements?.weightUnit || 'lbs'}
          value={answers.targetWeight}
          onSave={(v) => goNext(v)}
        />
      );
    }

    if (step.type === 'age-input') {
      return <AgeInputStep value={answers.age} onSave={(v) => goNext(v)} />;
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header showBack onBack={goBack} progress={progress} title={tr(step.title)} />
      <main className="px-4 md:px-8 py-6 md:py-10">
        <div className="max-w-3xl mx-auto">
          {step.subtitle && (
            <p className="text-center text-white/70 -mt-2 mb-6 max-w-md mx-auto">
              {tr(step.subtitle)}
            </p>
          )}
          <div className="fade-in-up" key={step.id}>
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuizPage;
